import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import html2canvas from "html2canvas";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bug, X, Camera, Send, Loader2, Pen, ArrowUpRight, Square, Type as TypeIcon, Undo2, Trash2 } from "lucide-react";

const TYPES = ["bug", "improvement", "question", "flaw"];
const SEVERITIES = ["Low", "Normal", "High", "Critical"];
const COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7", "#111827", "#ffffff"];

const AnnotationCanvas = forwardRef(({ src }, ref) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const shapesRef = useRef([]);
  const drawingRef = useRef(null);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#ef4444");
  const [size, setSize] = useState(4);

  const redraw = () => {
    const canvas = canvasRef.current, img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const s of shapesRef.current) drawShape(ctx, s);
    if (drawingRef.current) drawShape(ctx, drawingRef.current);
  };

  const drawShape = (ctx, s) => {
    ctx.strokeStyle = s.color; ctx.fillStyle = s.color; ctx.lineWidth = s.size; ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (s.type === "pen") {
      ctx.beginPath();
      s.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
    } else if (s.type === "rect") {
      ctx.strokeRect(s.x1, s.y1, s.x2 - s.x1, s.y2 - s.y1);
    } else if (s.type === "arrow") {
      const { x1, y1, x2, y2 } = s; const a = Math.atan2(y2 - y1, x2 - x1); const head = 8 + s.size * 2;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(a - Math.PI / 6), y2 - head * Math.sin(a - Math.PI / 6));
      ctx.lineTo(x2 - head * Math.cos(a + Math.PI / 6), y2 - head * Math.sin(a + Math.PI / 6));
      ctx.closePath(); ctx.fill();
    } else if (s.type === "text") {
      ctx.font = `600 ${s.size}px 'Plus Jakarta Sans', sans-serif`;
      ctx.textBaseline = "top";
      ctx.fillText(s.text, s.x, s.y);
    }
  };

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      redraw();
    };
    img.src = src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const pos = (e) => {
    const canvas = canvasRef.current; const r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvas.width / r.width), y: (e.clientY - r.top) * (canvas.height / r.height) };
  };

  const onDown = (e) => {
    const p = pos(e);
    if (tool === "text") {
      const text = window.prompt("Text to add:");
      if (text) { shapesRef.current.push({ type: "text", x: p.x, y: p.y, color, size: Math.max(16, size * 4), text }); redraw(); }
      return;
    }
    if (tool === "pen") drawingRef.current = { type: "pen", color, size, points: [p] };
    else drawingRef.current = { type: tool, color, size, x1: p.x, y1: p.y, x2: p.x, y2: p.y };
  };
  const onMove = (e) => {
    if (!drawingRef.current) return;
    const p = pos(e);
    if (drawingRef.current.type === "pen") drawingRef.current.points.push(p);
    else { drawingRef.current.x2 = p.x; drawingRef.current.y2 = p.y; }
    redraw();
  };
  const onUp = () => { if (drawingRef.current) { shapesRef.current.push(drawingRef.current); drawingRef.current = null; redraw(); } };

  useImperativeHandle(ref, () => ({
    getBlob: () => new Promise((res) => canvasRef.current.toBlob((b) => res(b), "image/png")),
  }));

  const TOOLS = [
    { id: "pen", icon: Pen, label: "Pen" },
    { id: "arrow", icon: ArrowUpRight, label: "Arrow" },
    { id: "rect", icon: Square, label: "Box" },
    { id: "text", icon: TypeIcon, label: "Text" },
  ];

  return (
    <div data-testid="bug-annotation">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-1">
          {TOOLS.map((t) => (
            <button key={t.id} type="button" title={t.label} data-testid={`bug-tool-${t.id}`} onClick={() => setTool(t.id)}
              className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${tool === t.id ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              <t.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {COLORS.map((c) => (
            <button key={c} type="button" data-testid={`bug-color-${c}`} onClick={() => setColor(c)}
              className={`h-6 w-6 rounded-full border ${color === c ? "ring-2 ring-offset-1 ring-slate-800" : "border-slate-200"}`} style={{ background: c }} />
          ))}
        </div>
        <input type="range" min="2" max="12" value={size} onChange={(e) => setSize(Number(e.target.value))} className="w-24 accent-indigo-600" title="Stroke width" data-testid="bug-stroke-size" />
        <div className="ml-auto flex items-center gap-1">
          <Button type="button" variant="outline" size="sm" data-testid="bug-undo" onClick={() => { shapesRef.current.pop(); redraw(); }}><Undo2 className="h-4 w-4 mr-1" /> Undo</Button>
          <Button type="button" variant="outline" size="sm" data-testid="bug-clear" onClick={() => { shapesRef.current = []; redraw(); }}><Trash2 className="h-4 w-4 mr-1" /> Clear</Button>
        </div>
      </div>
      <div className="max-h-[46vh] overflow-auto rounded-lg border border-slate-200 bg-slate-50">
        <canvas
          ref={canvasRef}
          data-testid="bug-canvas"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          className="w-full h-auto touch-none cursor-crosshair block"
        />
      </div>
    </div>
  );
});
AnnotationCanvas.displayName = "AnnotationCanvas";

export default function BugReporter() {
  const { branding } = useBranding();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("Normal");
  const [shot, setShot] = useState(null);
  const [sending, setSending] = useState(false);
  const annRef = useRef(null);

  const reset = () => { setType("bug"); setTitle(""); setDescription(""); setSeverity("Normal"); setShot(null); };

  if (branding?.bug_reporting?.enabled === false) return null;

  const capture = async () => {
    setCapturing(true);
    await new Promise((r) => setTimeout(r, 380));
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true, backgroundColor: "#ffffff", scale: 1, logging: false,
        windowWidth: document.documentElement.clientWidth,
        ignoreElements: (el) => el?.getAttribute?.("data-bug-ignore") === "1",
      });
      const maxW = 1100;
      let out = canvas;
      if (canvas.width > maxW) {
        const s = maxW / canvas.width;
        const c2 = document.createElement("canvas");
        c2.width = maxW; c2.height = Math.round(canvas.height * s);
        c2.getContext("2d").drawImage(canvas, 0, 0, c2.width, c2.height);
        out = c2;
      }
      setShot(out.toDataURL("image/png"));
    } catch (e) {
      toast.error("Couldn't capture the page — you can still send the report without a screenshot.");
    } finally {
      setCapturing(false);
    }
  };

  const send = async () => {
    if (!title.trim()) { toast.error("Add a short title first"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("title", title);
      fd.append("description", description);
      fd.append("severity", severity);
      fd.append("page_url", window.location.href);
      fd.append("meta", `${navigator.userAgent} · ${window.screen.width}x${window.screen.height}`);
      if (shot && annRef.current) {
        const blob = await annRef.current.getBlob();
        if (blob) fd.append("screenshot", blob, "screenshot.png");
      }
      const r = await api.post("/issues/report", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data?.card_url ? "Card created in Trello" : "Report sent");
      setOpen(false); reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the report");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-bug-ignore="1"
        data-testid="bug-launcher"
        onClick={() => setOpen(true)}
        title="Report an issue"
        className="fixed bottom-6 right-6 z-[95] h-14 w-14 rounded-full grid place-items-center text-white shadow-lg shadow-rose-500/30 hover:scale-105 active:scale-95 transition-transform"
        style={{ background: "linear-gradient(135deg, #f43f5e, #e11d48)" }}
      >
        <Bug className="h-6 w-6" />
      </button>

      {open && !capturing && (
        <div data-bug-ignore="1" className="fixed inset-0 z-[130] flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-10" data-testid="bug-modal">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
              <Bug className="h-5 w-5 text-rose-500" />
              <h2 className="font-admin-head text-lg font-bold text-slate-900">Report an issue</h2>
              <button data-testid="bug-close" onClick={() => setOpen(false)} className="ml-auto h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TYPES.map((t) => (
                  <button key={t} data-testid={`bug-type-${t}`} onClick={() => setType(t)}
                    className={`h-10 rounded-lg text-sm font-medium border capitalize transition-colors ${type === t ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t}</button>
                ))}
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wide">Title</Label>
                <Input data-testid="bug-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" className="mt-1" />
              </div>

              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wide">Description</Label>
                <Textarea data-testid="bug-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What happened, what did you expect?" className="mt-1 min-h-[110px]" />
              </div>

              <div className="flex items-center gap-3">
                <Label className="text-slate-600 text-xs uppercase tracking-wide">Severity</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger className="h-9 w-40" data-testid="bug-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {!shot ? (
                <button data-testid="bug-capture" onClick={capture} className="w-full rounded-xl border-2 border-dashed border-slate-200 py-4 text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-2">
                  <Camera className="h-5 w-5" /> Capture screenshot of this page
                </button>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Mark up the screenshot</p>
                    <button data-testid="bug-recapture" onClick={capture} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Recapture</button>
                  </div>
                  <AnnotationCanvas ref={annRef} src={shot} />
                </div>
              )}
            </div>

            <div className="flex gap-3 border-t border-slate-100 px-6 py-4">
              <Button variant="outline" className="flex-1" data-testid="bug-cancel" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="flex-1 bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="bug-send" onClick={send} disabled={sending}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send to support</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
